/**
 * AI Assist Panel - Phase B2
 * Desktop: Fixed right-side panel with tabs
 * Mobile: FAB with slide-up sheet
 * 
 * Provides 4 AI assistance features:
 * - Suggest: Generate field content
 * - Validate: Check content quality
 * - Translate: EN↔BM translation
 * - Rewrite: Improve tone/clarity
 */

import React, { useState } from 'react';
import { EMZI_COLORS, EMZI_SHADOWS } from '../styles/emziCI';
import * as aiAssistAPI from '../api/aiAssist';

const AIAssistPanel = ({
  packType,
  packData,
  onApplySuggestion,
  isMobile = false,
}) => {
  const [activeTab, setActiveTab] = useState('suggest');
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Form states for each tab
  const [suggestField, setSuggestField] = useState('');
  const [validateContent, setValidateContent] = useState(packData);
  const [translateText, setTranslateText] = useState('');
  const [translateLang, setTranslateLang] = useState({ source: 'en', target: 'ms' });
  const [rewriteText, setRewriteText] = useState('');
  const [rewriteIntent, setRewriteIntent] = useState('tone');

  const tabs = [
    { key: 'suggest', icon: '✨', label: 'Suggest' },
    { key: 'validate', icon: '✓', label: 'Validate' },
    { key: 'translate', icon: '🌐', label: 'Translate' },
    { key: 'rewrite', icon: '✍️', label: 'Rewrite' },
  ];

  const handleSuggest = async () => {
    if (!suggestField) {
      setError('Please enter a field name');
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const response = await aiAssistAPI.suggestContent(
        packType,
        suggestField,
        packData
      );
      setResult(response);
    } catch (err) {
      const errorMsg = err.message || 'Failed to generate suggestion';
      if (errorMsg.includes('403') || errorMsg.includes('401') || errorMsg.toLowerCase().includes('auth')) {
        setError('⚠️ Session expired. Please refresh the page and login again.');
      } else {
        setError(errorMsg);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleValidate = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await aiAssistAPI.validateContent(packType, packData);
      setResult(response);
    } catch (err) {
      const errorMsg = err.message || 'Failed to validate content';
      if (errorMsg.includes('403') || errorMsg.includes('401') || errorMsg.toLowerCase().includes('auth')) {
        setError('⚠️ Session expired. Please refresh the page and login again.');
      } else {
        setError(errorMsg);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleTranslate = async () => {
    if (!translateText) {
      setError('Please enter text to translate');
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const response = await aiAssistAPI.translateContent(
        translateText,
        translateLang.source,
        translateLang.target,
        packType,
        { product_name: packData?.name?.en || '', brand_terms: [] }
      );
      setResult(response);
    } catch (err) {
      const errorMsg = err.message || 'Failed to translate content';
      if (errorMsg.includes('403') || errorMsg.includes('401') || errorMsg.toLowerCase().includes('auth')) {
        setError('⚠️ Session expired. Please refresh the page and login again.');
      } else {
        setError(errorMsg);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRewrite = async () => {
    if (!rewriteText) {
      setError('Please enter text to rewrite');
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const response = await aiAssistAPI.rewriteContent(
        rewriteText,
        rewriteIntent,
        packType,
        packData
      );
      setResult(response);
    } catch (err) {
      const errorMsg = err.message || 'Failed to rewrite content';
      if (errorMsg.includes('403') || errorMsg.includes('401') || errorMsg.toLowerCase().includes('auth')) {
        setError('⚠️ Session expired. Please refresh the page and login again.');
      } else {
        setError(errorMsg);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleApply = () => {
    if (!result) return;

    const value = result.suggestion || result.translated_text || result.rewritten_text;
    if (value && onApplySuggestion) {
      onApplySuggestion(activeTab, value, result);
      setResult(null);
      if (isMobile) setIsOpen(false);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'suggest':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div>
              <label style={{
                display: 'block',
                fontSize: '13px',
                fontWeight: '500',
                color: EMZI_COLORS.textSecondary,
                marginBottom: '6px',
              }}>
                Field Name
              </label>
              <input
                type="text"
                value={suggestField}
                onChange={(e) => setSuggestField(e.target.value)}
                placeholder="e.g., features, benefits, hooks"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: `1px solid ${EMZI_COLORS.border}`,
                  borderRadius: '6px',
                  fontSize: '14px',
                }}
              />
            </div>
            <button
              onClick={handleSuggest}
              disabled={isLoading}
              style={{
                padding: '10px 16px',
                backgroundColor: isLoading ? EMZI_COLORS.textMuted : EMZI_COLORS.primary,
                color: EMZI_COLORS.textWhite,
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: isLoading ? 'not-allowed' : 'pointer',
              }}
            >
              {isLoading ? '✨ Generating...' : '✨ Generate Suggestion'}
            </button>
          </div>
        );

      case 'validate':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <p style={{ fontSize: '13px', color: EMZI_COLORS.textSecondary }}>
              Validate the current pack content for quality, compliance, and completeness.
            </p>
            <button
              onClick={handleValidate}
              disabled={isLoading}
              style={{
                padding: '10px 16px',
                backgroundColor: isLoading ? EMZI_COLORS.textMuted : EMZI_COLORS.success,
                color: EMZI_COLORS.textWhite,
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: isLoading ? 'not-allowed' : 'pointer',
              }}
            >
              {isLoading ? '✓ Validating...' : '✓ Validate Content'}
            </button>
          </div>
        );

      case 'translate':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div>
              <label style={{
                display: 'block',
                fontSize: '13px',
                fontWeight: '500',
                color: EMZI_COLORS.textSecondary,
                marginBottom: '6px',
              }}>
                Text to Translate
              </label>
              <textarea
                value={translateText}
                onChange={(e) => setTranslateText(e.target.value)}
                placeholder="Enter text to translate..."
                rows={3}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: `1px solid ${EMZI_COLORS.border}`,
                  borderRadius: '6px',
                  fontSize: '14px',
                  resize: 'vertical',
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <select
                value={translateLang.source}
                onChange={(e) => setTranslateLang({ ...translateLang, source: e.target.value })}
                style={{
                  flex: 1,
                  padding: '8px',
                  border: `1px solid ${EMZI_COLORS.border}`,
                  borderRadius: '6px',
                  fontSize: '14px',
                }}
              >
                <option value="en">English</option>
                <option value="ms">Malay</option>
              </select>
              <span style={{ fontSize: '16px', color: EMZI_COLORS.textSecondary }}>→</span>
              <select
                value={translateLang.target}
                onChange={(e) => setTranslateLang({ ...translateLang, target: e.target.value })}
                style={{
                  flex: 1,
                  padding: '8px',
                  border: `1px solid ${EMZI_COLORS.border}`,
                  borderRadius: '6px',
                  fontSize: '14px',
                }}
              >
                <option value="en">English</option>
                <option value="ms">Malay</option>
              </select>
            </div>
            <button
              onClick={handleTranslate}
              disabled={isLoading}
              style={{
                padding: '10px 16px',
                backgroundColor: isLoading ? EMZI_COLORS.textMuted : EMZI_COLORS.info,
                color: EMZI_COLORS.textWhite,
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: isLoading ? 'not-allowed' : 'pointer',
              }}
            >
              {isLoading ? '🌐 Translating...' : '🌐 Translate'}
            </button>
            {result?.requires_review && (
              <div style={{
                padding: '8px 12px',
                backgroundColor: '#FFF4E6',
                border: `1px solid ${EMZI_COLORS.warning}`,
                borderRadius: '6px',
                fontSize: '12px',
                color: EMZI_COLORS.textPrimary,
              }}>
                ⚠️ <strong>Human Review Required:</strong> Please review translation before publishing.
              </div>
            )}
          </div>
        );

      case 'rewrite':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div>
              <label style={{
                display: 'block',
                fontSize: '13px',
                fontWeight: '500',
                color: EMZI_COLORS.textSecondary,
                marginBottom: '6px',
              }}>
                Text to Rewrite
              </label>
              <textarea
                value={rewriteText}
                onChange={(e) => setRewriteText(e.target.value)}
                placeholder="Enter text to improve..."
                rows={3}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: `1px solid ${EMZI_COLORS.border}`,
                  borderRadius: '6px',
                  fontSize: '14px',
                  resize: 'vertical',
                }}
              />
            </div>
            <div>
              <label style={{
                display: 'block',
                fontSize: '13px',
                fontWeight: '500',
                color: EMZI_COLORS.textSecondary,
                marginBottom: '6px',
              }}>
                Improvement Goal
              </label>
              <select
                value={rewriteIntent}
                onChange={(e) => setRewriteIntent(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: `1px solid ${EMZI_COLORS.border}`,
                  borderRadius: '6px',
                  fontSize: '14px',
                }}
              >
                <option value="tone">Professional Tone</option>
                <option value="clarity">Clarity & Readability</option>
                <option value="compliance">Regulatory Compliance</option>
                <option value="engagement">Engagement & Action</option>
                <option value="conciseness">Conciseness</option>
              </select>
            </div>
            <button
              onClick={handleRewrite}
              disabled={isLoading}
              style={{
                padding: '10px 16px',
                backgroundColor: isLoading ? EMZI_COLORS.textMuted : EMZI_COLORS.accent,
                color: EMZI_COLORS.textWhite,
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: isLoading ? 'not-allowed' : 'pointer',
              }}
            >
              {isLoading ? '✍️ Rewriting...' : '✍️ Rewrite'}
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  const renderResult = () => {
    if (!result) return null;

    return (
      <div style={{
        marginTop: '16px',
        padding: '12px',
        backgroundColor: EMZI_COLORS.surface,
        border: `1px solid ${EMZI_COLORS.border}`,
        borderRadius: '8px',
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '8px',
        }}>
          <span style={{ fontSize: '13px', fontWeight: '600', color: EMZI_COLORS.textPrimary }}>
            AI Result
          </span>
          <button
            onClick={handleApply}
            style={{
              padding: '6px 12px',
              backgroundColor: EMZI_COLORS.success,
              color: EMZI_COLORS.textWhite,
              border: 'none',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: '500',
              cursor: 'pointer',
            }}
          >
            Apply
          </button>
        </div>

        {activeTab === 'validate' && result.validation ? (
          <div style={{ fontSize: '13px', color: EMZI_COLORS.textPrimary }}>
            <div style={{
              padding: '8px',
              backgroundColor: result.validation.is_valid ? '#F0FDF4' : '#FEF2F2',
              borderRadius: '6px',
              marginBottom: '8px',
            }}>
              <strong>{result.validation.is_valid ? '✓ Valid' : '✗ Issues Found'}</strong>
            </div>
            {result.validation.issues?.length > 0 && (
              <div style={{ marginBottom: '8px' }}>
                <strong>Issues:</strong>
                <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                  {result.validation.issues.map((issue, i) => (
                    <li key={`item-${i}`}>{issue}</li>
                  ))}
                </ul>
              </div>
            )}
            {result.validation.recommendations?.length > 0 && (
              <div>
                <strong>Recommendations:</strong>
                <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                  {result.validation.recommendations.map((rec, i) => (
                    <li key={`item-${i}`}>{rec}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <div style={{
            padding: '10px',
            backgroundColor: EMZI_COLORS.background,
            borderRadius: '6px',
            fontSize: '13px',
            color: EMZI_COLORS.textPrimary,
            whiteSpace: 'pre-wrap',
            maxHeight: '200px',
            overflowY: 'auto',
          }}>
            {result.suggestion || result.translated_text || result.rewritten_text}
          </div>
        )}
      </div>
    );
  };

  // Mobile FAB + Sheet
  if (isMobile) {
    return (
      <>
        {/* FAB Button */}
        <button
          onClick={() => setIsOpen(true)}
          style={{
            position: 'fixed',
            bottom: 'calc(100px + env(safe-area-inset-bottom))',  // Clear bottom nav + badge + safe area
            right: '16px',
            width: '56px',
            height: '56px',
            borderRadius: '50%',
            backgroundColor: EMZI_COLORS.primary,
            color: EMZI_COLORS.textWhite,
            border: 'none',
            fontSize: '20px',
            fontWeight: '600',
            cursor: 'pointer',
            boxShadow: EMZI_SHADOWS.lg,
            zIndex: 9999,  // Above bottom nav and badge
            transition: 'transform 0.2s ease',
          }}
          onTouchStart={(e) => e.currentTarget.style.transform = 'scale(0.95)'}
          onTouchEnd={(e) => e.currentTarget.style.transform = 'scale(1)'}
        >
          AI
        </button>

        {/* Slide-up Sheet */}
        {isOpen && (
          <>
            {/* Overlay / Backdrop */}
            <div
              onClick={() => setIsOpen(false)}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 10000,  // Below sheet, above everything else
                touchAction: 'none',  // Prevent scroll
              }}
            />

            {/* Sheet Container */}
            <div style={{
              position: 'fixed',
              left: 0,
              right: 0,
              bottom: 0,
              maxHeight: '75vh',
              backgroundColor: EMZI_COLORS.background,
              borderTopLeftRadius: '16px',
              borderTopRightRadius: '16px',
              boxShadow: '0 -4px 24px rgba(0, 0, 0, 0.15)',
              zIndex: 10001,  // Above overlay
              display: 'flex',
              flexDirection: 'column',
              overflowY: 'hidden',  // Container shouldn't scroll
              paddingBottom: 'env(safe-area-inset-bottom)',  // iOS safe area
            }}>
              {/* Sheet Header */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '16px 20px',
                borderBottom: `1px solid ${EMZI_COLORS.border}`,
                flexShrink: 0,
              }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', color: EMZI_COLORS.textPrimary, margin: 0 }}>
                  AI Assist
                </h3>
                <button
                  onClick={() => setIsOpen(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '28px',
                    cursor: 'pointer',
                    color: EMZI_COLORS.textSecondary,
                    padding: 0,
                    lineHeight: 1,
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  ×
                </button>
              </div>

              {/* Scrollable Content */}
              <div style={{
                flex: 1,
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch',
                padding: '20px',
              }}>
                {/* Tabs */}
                <div style={{
                  display: 'flex',
                  gap: '8px',
                  marginBottom: '16px',
                  borderBottom: `1px solid ${EMZI_COLORS.border}`,
                  overflowX: 'auto',
                  paddingBottom: '8px',
                }}>
                  {tabs.map((tab) => (
                    <button
                      key={tab.key}
                      onClick={() => {
                        setActiveTab(tab.key);
                        setResult(null);
                        setError(null);
                      }}
                      style={{
                        padding: '10px 16px',
                        backgroundColor: 'transparent',
                        color: activeTab === tab.key ? EMZI_COLORS.primary : EMZI_COLORS.textSecondary,
                        border: 'none',
                        borderBottom: activeTab === tab.key ? `2px solid ${EMZI_COLORS.primary}` : '2px solid transparent',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {tab.icon} {tab.label}
                    </button>
                  ))}
                </div>

                {/* Error */}
                {error && (
                  <div style={{
                    padding: '10px',
                    backgroundColor: '#FEF2F2',
                    border: `1px solid ${EMZI_COLORS.error}`,
                    borderRadius: '6px',
                    marginBottom: '12px',
                    fontSize: '13px',
                    color: EMZI_COLORS.error,
                  }}>
                    {error}
                  </div>
                )}

                {/* Tab Content */}
                {renderTabContent()}

                {/* Result */}
                {renderResult()}
                
                {/* Bottom Spacer - Ensures content is not hidden behind nav */}
                <div style={{ minHeight: '100px' }}></div>
              </div>
            </div>
          </>
        )}
      </>
    );
  }

  // Desktop Sticky Panel
  return (
    <div style={{
      position: 'sticky',
      top: '20px',
      width: '320px',
      backgroundColor: EMZI_COLORS.background,
      border: `1px solid ${EMZI_COLORS.border}`,
      borderRadius: '12px',
      boxShadow: EMZI_SHADOWS.card,
      padding: '16px',
      maxHeight: 'calc(100vh - 40px)',
      overflowY: 'auto',
    }}>
      {/* Header */}
      <h3 style={{
        fontSize: '16px',
        fontWeight: '600',
        color: EMZI_COLORS.textPrimary,
        marginBottom: '12px',
      }}>
        AI Assist
      </h3>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        marginBottom: '16px',
      }}>
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => {
              setActiveTab(tab.key);
              setResult(null);
              setError(null);
            }}
            style={{
              padding: '10px 12px',
              backgroundColor: activeTab === tab.key ? EMZI_COLORS.primary : 'transparent',
              color: activeTab === tab.key ? EMZI_COLORS.textWhite : EMZI_COLORS.textSecondary,
              border: `1px solid ${activeTab === tab.key ? EMZI_COLORS.primary : EMZI_COLORS.border}`,
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              textAlign: 'left',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <span>{tab.icon}</span>
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Error */}
      {error && (
        <div style={{
          padding: '10px',
          backgroundColor: '#FEF2F2',
          border: `1px solid ${EMZI_COLORS.error}`,
          borderRadius: '6px',
          marginBottom: '12px',
          fontSize: '13px',
          color: EMZI_COLORS.error,
        }}>
          {error}
        </div>
      )}

      {/* Tab Content */}
      {renderTabContent()}

      {/* Result */}
      {renderResult()}

      {/* Legal Banner for Compliance/Health */}
      {(['compliance', 'health'].includes(packType)) && (
        <div style={{
          marginTop: '16px',
          padding: '10px',
          backgroundColor: '#FFF4E6',
          border: `1px solid ${EMZI_COLORS.warning}`,
          borderRadius: '6px',
          fontSize: '12px',
          color: EMZI_COLORS.textPrimary,
        }}>
          ⚖️ <strong>Legal Review Required:</strong> All AI-generated content for {packType} packs must be reviewed by compliance team.
        </div>
      )}
    </div>
  );
};

export default AIAssistPanel;
